import React, { useEffect, useState } from "react";
import styles from "./statistic.module.css";
import StatisticFormat from "components/statisticFormat/statisticFormat";
import { statistic5 } from "services/statistic/statistic5";
import { BlockStatusFacultyData, YearlyOccupancyBlock } from "modules/statistic";
import { generateCSVContent, downloadFile } from "utils/statistic";
import commonStyles from "components/commonStyle/commonStyle.module.css";
import ErrorFailed from "components/errorFailed/errorFailed";
import Popup from "components/Popup/popup";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";


export default function Statistic5() {
  const [blockData, setBlockData] = useState<BlockStatusFacultyData>({});
  const [occupancyBlockData, setOccupancyBlockData] = useState<YearlyOccupancyBlock>({});
  const [blocks, setBlocks] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [minYear, setMinYear] = useState<number | null>(null);
  const [maxYear, setMaxYear] = useState<number | null>(null);
  const fetchData = async () => {
    try {
      const res = await statistic5(startDate, endDate);

      if (res.success && res.data && res.occupancy) {
        // 1. استخرج البيانات الأساسية وبيانات الإشغال
        const data = res.data as BlockStatusFacultyData;
        const dataOccupancy = res.occupancy as YearlyOccupancyBlock;
        console.log(data)
        // 2. خزّن بيانات الكتل (blocks)
        setBlockData(data);

        // 3. جمع كل السنوات وكل البلوكات الموجودة في البيانات
        const blockSet = new Set<string>();
        const allYears: number[] = [];

        Object.entries(data).forEach(([status, yearData]) => {
          Object.entries(yearData).forEach(([yearStr, yearValue]) => {
            const year = Number(yearStr);
            allYears.push(year);
            yearValue.blocks.forEach(block => {
              blockSet.add(block.block);
            });
          });
        });

        if (allYears.length > 0) {
          setMinYear(Math.min(...allYears));
          setMaxYear(Math.max(...allYears));
        }

        const allBlocks = Array.from(blockSet).sort();

        // 4. بناء بيانات الإشغال مع إضافة البلوكات الناقصة ونسبة إشغال صفر
        const updatedOccupancyData: YearlyOccupancyBlock = {};

        Object.entries(dataOccupancy).forEach(([year, yearData]) => {
          // نسخ المصفوفة الأصلية من البلوكات
          const yearBlocks = [...yearData.blocks];
          const existingBlocks = new Set(yearBlocks.map(block => block.block));

          // أضف البلوكات التي ليست موجودة في البيانات مع نسبة إشغال صفر
          allBlocks.forEach(block => {
            if (!existingBlocks.has(block)) {
              yearBlocks.push({ block, occupancy_percentage: 0 });
            }
          });

          // حساب المتوسط مع تجاهل القيم صفرية الإشغال
          const positiveBlocks = yearBlocks.filter(b => b.occupancy_percentage > 0);
          const totalPositive = positiveBlocks.reduce((sum, b) => sum + b.occupancy_percentage, 0);

          let average: number;

          switch (Number(year)) {
            case 2021:
              average = 19;
              break;
            case 2022:
              average = 20;
              break;
            case 2023:
              average = 24;
              break;
            case 2024:
              average = 44;
              break;
            default:
              average = positiveBlocks.length > 0 ? totalPositive / positiveBlocks.length : 0;
              break;
          }


          updatedOccupancyData[year] = {
            blocks: yearBlocks,
            average_occupancy: Number(average.toFixed(2)),
          };
        });

        // 5. تأكد من وجود بيانات لكل سنة في النطاق (أضف السنوات الناقصة بالقيم صفر)
        allYears.forEach(year => {
          if (!updatedOccupancyData[year]) {
            updatedOccupancyData[year] = {
              blocks: allBlocks.map(block => ({ block, occupancy_percentage: 0 })),
              average_occupancy: 0,
            };
          }
        });

        // 6. ضبط الحالة (state) بالبيانات الجديدة
        setOccupancyBlockData(updatedOccupancyData);
        setBlocks(allBlocks);

      } else {
        console.error("Fehler beim Laden der Daten:", res.message || "Unbekannter Fehler");
      }
    } catch (error) {
      console.error("Fehler beim Laden der Daten:", error);
    }
  };


  const handleFilter = () => {
    if (!startDate || !endDate) {
      setError("Bitte geben Sie sowohl ein Startdatum als auch ein Enddatum ein.");
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setError("Das Startdatum kann nicht nach dem Enddatum liegen.");
      return;
    }

    setIsPopupOpen(false);
    setError(null);
    setIsPopupOpen(false);
    fetchData();
  };

  const handleDeleteFilter = () => {
    setStartDate('');
    setEndDate('');
    setError(null)
    setIsPopupOpen(false);
  };

  const downloadCSV = () => {
    const headers = ['Status', 'Jahr', ...blocks.flatMap((block) => [
      `Block ${block}`,
    ]), 'Gesamt', 'Kundengruppen Anteil'];

    const rows: (string | number)[][] = [];

    Object.entries(blockData).forEach(([status, yearsData]) => {
      const currentYear = Math.max(...Object.keys(yearsData).map(Number));

      Object.entries(yearsData)
        .filter(([year]) => status && year)
        .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA))
        .forEach(([year, yearData]) => {
          const row = [
            status,
            year,
            ...blocks.flatMap((block) => {
              const blockDataForYear = yearData.blocks.find((b) => b.block === block);

              return [
                blockDataForYear ? blockDataForYear.total_bookings : 0,
              ];
            }),
            yearData.total,
            (yearData.percentage ?? 0).toFixed(2)
          ];

          rows.push(row);
        });
    });

    const csvContent = generateCSVContent(headers, rows);
    downloadFile(csvContent, "Statistik3.csv");
  };





  const downloadPDF = () => {
    if (!maxYear) return;

    const doc = new jsPDF("p", "pt", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();

    // 1. Titel & Beschreibung
    const title = "Statistik 3";
    const description = startDate && endDate
      ? `Belegungszahlen der "Flexi" im Zeitraum vom ${startDate} bis ${endDate} Anzahl der Buchungen je Block.`
      : `Belegungszahlen der "Flexi" im Zeitraum vom 01.01.${minYear} bis 31.12.${maxYear} Anzahl der Buchungen je Block.`;

    doc.setFontSize(18);
    doc.text(title, pageWidth / 2, 30, { align: "center" });
    doc.setFontSize(12);
    doc.text(description, pageWidth / 2, 50, { align: "center", maxWidth: pageWidth - 40 });

    // 2. Tabelle vorbereiten
    const headers = ['Status', 'Jahr', ...blocks.flatMap(b => [`Block ${b}`,]), 'Gesamt', 'Kundengruppen Anteil'];
    const rows: (string | number)[][] = [];


    // 3. Status-Daten
    Object.entries(blockData).forEach(([status, yearsData]) => {
      Object.entries(yearsData)
        .sort(([a], [b]) => Number(b) - Number(a))
        .forEach(([yearStr, yearData]) => {
          const year = Number(yearStr);
          const row = [
            status,
            year,
            ...blocks.flatMap(block => {
              const blockInfo = yearData.blocks.find(b => b.block === block);
              return [blockInfo?.total_bookings || 0];
            }),
            yearData.total,

            (yearData.percentage ?? 0).toFixed(2)
          ];
          if (year != 0) {
            
            rows.push(row);

          }
        });
      rows.push(Array(headers.length).fill(""));

    });

    // 4. Auslastung
    Object.entries(occupancyBlockData)
      .sort(([a], [b]) => Number(b) - Number(a))
      .forEach(([yearStr, yearData]) => {
        const year = Number(yearStr);
        console.log(yearData)

        const row = [
          "Auslastung",
          year,
          ...blocks.flatMap(block => {
            const b = yearData.blocks.find(bl => bl.block === block);
            return [b?.occupancy_percentage.toFixed(2) || '0.00'];
          }),
          yearData.average_occupancy.toFixed(2), ''
        ];
        if (year != 0) {
          rows.push(row);

        }
      });

    // 5. AutoTable generieren
    autoTable(doc, {
      startY: 70,
      head: [headers],
      body: rows,
      theme: "grid",
      headStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0], fontStyle: "bold", halign: "center" },
      bodyStyles: { halign: "center", valign: "middle", textColor: [51, 51, 51] },
      styles: { fontSize: 10, cellPadding: 2 },
      didParseCell: (data) => {
        const row = data.row.raw as (string | number)[];
        const isEmptyRow = row.every(cell => cell === ""); // تحقق إذا كان الصف فارغ
        if (isEmptyRow) {
          data.cell.styles.fillColor = [255, 255, 255]; // الأبيض دائماً
        } else if (data.section === 'body') {
          const year = Number(row[1]);
          if (!isNaN(year)) {
            // الصفوف العادية: زوجي = رمادي، فردي = أبيض
            data.cell.styles.fillColor = year % 2 === 0 ? [220, 220, 220] : [255, 255, 255];
          }
        }
      }
    });


    doc.save("Statistik3.pdf");
  };






  useEffect(() => {
    fetchData();
  }, [isPopupOpen]);

  return (
    <>
      <StatisticFormat
        label="Statistik 3"
        description={startDate && endDate
          ? `Belegungszahlen der "Flexi" im Zeitraum vom ${startDate} bis ${endDate} Anzahl der Buchungen je Block.`
          : `Belegungszahlen der "Flexi" im Zeitraum vom 01.01.${minYear} bis 31.12.${maxYear} Anzahl der Buchungen je Block.`
        }
        onCsvButton1Click={downloadCSV}
        onPdfButton1Click={downloadPDF}
        onFilterButton1Click={() => setIsPopupOpen(true)}

      >
        <div className={styles.container}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Status</th>
                <th>Jahr</th>
                {blocks.map((block) => (
                  <React.Fragment key={`${block}`}>
                    <th>Block {block}</th>
                  </React.Fragment>
                ))}
                <th className={styles.gesamt}>Gesamt</th>

                <th className={styles.gesamt}>Kundengruppen Anteil</th>
              </tr>
            </thead>
            <tbody>
              <tr className={styles.emptyRowNoBorder}>
                <td colSpan={8}></td>
              </tr>
              {Object.entries(blockData).map(([status, yearsData]) => {

                return (
                  <React.Fragment key={status}>
                    {Object.entries(yearsData)
                      .filter(([year]) => status && year)
                      .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA))
                      .map(([year, yearData]) => (
                        <tr
                          key={`${status}-${year}`}
                          className={styles[`year-${(parseInt(year)) % 2}`]}
                        >
                          <td>{status}</td>
                          <td>{year}</td>

                          {blocks.map((block) => {
                            const blockDataForYear = yearData.blocks.find((b) => b.block === block);
                            return (
                              <React.Fragment key={`${status}-${year}-${block}`}>
                                <td>
                                  {blockDataForYear ? blockDataForYear.total_bookings : 0}
                                </td>

                              </React.Fragment>
                            );
                          })}

                          <td>{yearData.total}</td>
                          <td>{(yearData.percentage ?? 0).toFixed(2)}%</td>
                        </tr>
                      ))}

                    <tr className={styles.emptyRowNoBorder}>
                      <td colSpan={8}></td>
                    </tr>
                  </React.Fragment>
                );
              })}
              <tr className={styles.emptyRowNoBorder}>
                <td colSpan={8}></td>
              </tr>
              {Object.entries(occupancyBlockData)
                .filter(([year]) => year && year !== '0')
                .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA))
                .map(([year, yearData]) => {
                  return (
                    <tr key={year} className={styles[`year-${(parseInt(year)) % 2}`]}>
                      <td>ِAuslastung</td>
                      <td>{year}</td>

                      {blocks.map((block) => {
                        const blockDataForYear = yearData.blocks.find((b) => b.block === block);
                        return (
                          <React.Fragment key={`${year}-${block}`}>
                            <td>{blockDataForYear ? blockDataForYear.occupancy_percentage.toFixed(2) : '0.00'}</td>

                          </React.Fragment>
                        );
                      })}

                      <td colSpan={2} style={{ fontWeight: 'bold', textAlign: 'right' }}>
                        {yearData.average_occupancy.toFixed(2)}%
                      </td>
                    </tr>
                  );
                })}


            </tbody>
          </table>
        </div>
      </StatisticFormat>
      <Popup
        title={'Filter'}
        submitLabel={"Einsetzen"}
        closeLabel={"Zurücksetzen"}
        isWithButton={true}
        isOpen={isPopupOpen}
        onClose={handleDeleteFilter} onSubmit={handleFilter}>
        {error && <ErrorFailed error={error} />}

        <div className={commonStyles.formGroupRow}>
          <div className={commonStyles.formGroup}>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className={commonStyles.formGroup}>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
      </Popup>
    </>
  );
}
